import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreVertical,
  Search,
  SlidersHorizontal,
  Tags,
  X,
} from 'lucide-react';
import {
  CUSTOMER_SEGMENTS,
  CUSTOMER_SORT_FIELDS,
  CUSTOMER_UNASSIGNED,
  type Customer,
  type CustomerSegment,
  type CustomerSortField,
} from '@tailoring-crm/shared';
import { useAuth } from '../../lib/auth/auth-context.js';
import { customerExportPath } from '../../lib/customers/customers-api.js';
import {
  useCustomerSearch,
  useCustomerSegmentation,
  useCustomerTags,
} from '../../lib/customers/use-customers.js';
import { useResolvedCustomFields } from '../../lib/custom-fields/use-custom-fields.js';
import { TAG_COLOUR_CLASSES } from '../../lib/customers/tag-colours.js';
import { formatCurrency, formatNumber } from '../../lib/format.js';
import { useStaffList } from '../../lib/staff/use-staff.js';
import {
  cn,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  ListSkeleton,
} from '@tailoring-crm/ui';
import { TaskDialog } from '../activity/task-dialog.js';
import { WorkspaceThemeToggle } from '../layout/workspace-theme.js';
import { CustomerFormDialog } from './customer-form-dialog.js';
import { CustomerTable } from './customer-table.js';
import { ManageCustomerTagsSheet } from './manage-customer-tags-sheet.js';

/**
 * The customer book: a toolbar, a sheet, and a status line that stays put.
 *
 * The five tiles that used to head this screen are now two things. Their QUESTION — "show me the
 * dormant ones" — is the Segment filter, which the tiles could never answer because they only
 * counted. Their FIGURES are on the status line, where a sheet keeps its totals.
 */

/** Capped at 100, the most the server's pagination schema accepts. */
const PAGE_SIZES = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_SORT: CustomerSortField = 'recent';

/** The direction a heading sorts in when first clicked: names A-first, figures biggest-first. */
const FIRST_DIRECTION: Readonly<Record<CustomerSortField, 'asc' | 'desc'>> = Object.freeze({
  recent: 'desc',
  name: 'asc',
  lifetimeValue: 'desc',
  lastOrder: 'desc',
  orderCount: 'desc',
});

const isSegment = (value: string | null): value is CustomerSegment =>
  value !== null && (CUSTOMER_SEGMENTS as readonly string[]).includes(value);

const isSortField = (value: string | null): value is CustomerSortField =>
  value !== null && (CUSTOMER_SORT_FIELDS as readonly string[]).includes(value);

/** The tile each segment's count comes from on the summary. */
const SUMMARY_KEY = {
  new: 'new',
  active: 'active',
  dormant: 'dormant',
  high_value: 'highValue',
} as const satisfies Record<CustomerSegment, string>;

export function CustomersPanel(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const navigate = useNavigate();
  /** The API refuses the export to anybody but the owner (`customers.export`); so does this. */
  const canExport = role === 'owner';
  // Mirrors `custom_fields.write` in the permission matrix.
  const canConfigure = role === 'owner' || role === 'manager';
  // Mirrors `customers.write`, which the shop-wide rename and remove require.
  const canEditTags = role === 'owner' || role === 'manager';
  const [managingTags, setManagingTags] = useState(false);

  /**
   * What is being asked lives in the URL: opening a customer is a navigation, and the search,
   * the segment and the tags have to survive the trip there and back. Every change REPLACES the
   * address, so Back still means "the customer I was just looking at". Where you had got to in
   * the list — the page and its size — stays ordinary state: it is not worth sharing in a
   * link.
   */
  const [urlParams, setUrlParams] = useSearchParams();
  const query = urlParams.get('q') ?? '';
  const segmentParam = urlParams.get('segment');
  const segment: CustomerSegment | null = isSegment(segmentParam) ? segmentParam : null;
  // Keyed on the joined value, so the list is a new array only when the tags actually change.
  const tagsKey = urlParams.getAll('tags').join('\u0000');
  const activeTags = useMemo(() => (tagsKey ? tagsKey.split('\u0000') : []), [tagsKey]);
  /** A staff member's id, `unassigned`, or nothing for everybody's. */
  const assignedTo = urlParams.get('assignedTo') ?? '';
  /**
   * "Enquiries": people who have never ordered. An enquiry is no longer a record of its own — it
   * is a customer with no orders yet — so this is how the counter finds the people to chase.
   */
  const enquiriesOnly = urlParams.get('orders') === 'none';
  const sortParam = urlParams.get('sortBy');
  const sortBy: CustomerSortField = isSortField(sortParam) ? sortParam : DEFAULT_SORT;
  const sortDir: 'asc' | 'desc' =
    urlParams.get('sortDir') === 'asc' || urlParams.get('sortDir') === 'desc'
      ? (urlParams.get('sortDir') as 'asc' | 'desc')
      : FIRST_DIRECTION[sortBy];

  const update = (change: (params: URLSearchParams) => void): void => {
    setUrlParams(
      (current) => {
        const params = new URLSearchParams(current);
        change(params);
        return params;
      },
      { replace: true },
    );
  };

  const setQuery = (next: string): void =>
    update((params) => (next ? params.set('q', next) : params.delete('q')));
  const setSegment = (next: CustomerSegment | null): void =>
    update((params) => (next ? params.set('segment', next) : params.delete('segment')));
  const setAssignedTo = (next: string): void =>
    update((params) => (next ? params.set('assignedTo', next) : params.delete('assignedTo')));
  const toggleEnquiries = (): void =>
    update((params) => (enquiriesOnly ? params.delete('orders') : params.set('orders', 'none')));
  const toggleTag = (tag: string): void =>
    update((params) => {
      const next = activeTags.includes(tag)
        ? activeTags.filter((one) => one !== tag)
        : [...activeTags, tag];
      params.delete('tags');
      for (const one of next) params.append('tags', one);
    });

  /**
   * A tag renamed or removed in the manage dialog must not leave the filter asking for a tag
   * nobody carries any more — that would show an empty list with no clue why.
   */
  const replaceActiveTag = (from: string, to: string | null): void => {
    if (!activeTags.includes(from)) return;
    update((params) => {
      const next = activeTags
        .map((one) => (one === from ? to : one))
        .filter((one): one is string => one !== null);
      params.delete('tags');
      for (const one of new Set(next)) params.append('tags', one);
    });
  };

  /** Clicking the sorted column flips direction; clicking another switches to it. */
  const onSort = (field: CustomerSortField): void =>
    update((params) => {
      const dir = field === sortBy ? (sortDir === 'asc' ? 'desc' : 'asc') : FIRST_DIRECTION[field];
      params.set('sortBy', field);
      params.set('sortDir', dir);
    });

  /**
   * `/customers?id=…` is where a customer used to be opened, back when they opened in a dialog
   * over this list. That address is in people's history and pasted into chats, so it is honoured
   * — as a redirect to the customer's own page, replacing rather than pushing.
   */
  const requestedId = urlParams.get('id');
  useEffect(() => {
    if (requestedId) navigate(`/customers/${requestedId}`, { replace: true });
  }, [requestedId, navigate]);

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [followingUp, setFollowingUp] = useState<Customer | null>(null);

  const searchParams = useMemo(
    () => ({
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(activeTags.length > 0 ? { tags: activeTags } : {}),
      ...(segment ? { segment } : {}),
      ...(assignedTo ? { assignedTo } : {}),
      ...(enquiriesOnly ? { orders: 'none' as const } : {}),
      sortBy,
      sortDir,
      page,
      pageSize,
    }),
    [query, activeTags, segment, assignedTo, enquiriesOnly, sortBy, sortDir, page, pageSize],
  );

  const results = useCustomerSearch(searchParams);
  const tagsQuery = useCustomerTags();
  // Shop-wide, so it is asked for once and NOT re-asked as the search box is typed into.
  const summary = useCustomerSegmentation().data;
  const { fields, labelOf } = useResolvedCustomFields('customers');
  const staff = useStaffList();
  const assignees = (staff.data?.items ?? []).filter(
    (member) => member.active && member.role !== 'tailor',
  );

  /** What the sheet shows: in the shop's order, minus anything it hid or kept off the table. */
  const columns = useMemo(
    () => fields.filter((field) => !field.hidden && field.showInTable),
    [fields],
  );

  const items = results.data?.items ?? [];
  const total = results.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstOnPage = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastOnPage = Math.min(page * pageSize, total);
  const narrowed =
    query.trim().length > 0 ||
    segment !== null ||
    activeTags.length > 0 ||
    assignedTo !== '' ||
    enquiriesOnly;

  // Narrowing the list makes the page you were on meaningless, so it goes back to the first.
  useEffect(() => {
    setPage(1);
  }, [query, segment, activeTags, assignedTo, enquiriesOnly, sortBy, sortDir, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  /**
   * "Add" carries what was typed across, so "no match" flows straight into a half-filled form
   * rather than making the counter type the number a second time.
   */
  const createHref = query.trim()
    ? `/customers/new?prefill=${encodeURIComponent(query.trim())}`
    : '/customers/new';

  /** The booking screen, with this customer already filled in. */
  const newOrderFor = (customer: Customer): void => {
    const params = new URLSearchParams({
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.primaryPhone,
    });
    navigate(`/orders/new?${params.toString()}`);
  };

  const segmentCount = (value: CustomerSegment): string =>
    summary ? ` (${formatNumber(summary[SUMMARY_KEY[value]])})` : '';

  const tags = tagsQuery.data?.tags ?? [];

  return (
    <div className="record-sheet md:min-h-0 md:flex-1">
      <div className="record-sheet-toolbar flex shrink-0 flex-wrap items-end gap-2">
        <div className="flex min-w-[14rem] max-w-sm flex-1 flex-col gap-1">
          <label htmlFor="customer-search" className="text-sm font-medium">
            {t('customers.searchLabel')}
          </label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="customer-search"
              // `search` so the phone's keyboard offers a search key. Focused on arrival: the
              // counter's first move here is almost always typing a number.
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('customers.searchPlaceholder')}
              className="pl-9 pr-10"
              autoFocus
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={t('customers.clearSearch')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="customer-segment-filter" className="text-sm font-medium">
            {t('customers.filterSegment')}
          </label>
          <select
            id="customer-segment-filter"
            value={segment ?? 'all'}
            onChange={(event) =>
              setSegment(
                event.target.value === 'all' ? null : (event.target.value as CustomerSegment),
              )
            }
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="all">
              {t('customers.segments.total')}
              {summary ? ` (${formatNumber(summary.total)})` : ''}
            </option>
            {CUSTOMER_SEGMENTS.map((value) => (
              <option key={value} value={value}>
                {t(`customers.segments.${SUMMARY_KEY[value]}`)}
                {segmentCount(value)}
              </option>
            ))}
          </select>
        </div>

        {/* Whose customers: one staff member's, or the ones nobody is looking after yet. */}
        <div className="flex flex-col gap-1">
          <label htmlFor="customer-assignee-filter" className="text-sm font-medium">
            {t('customers.filterAssignee')}
          </label>
          <select
            id="customer-assignee-filter"
            value={assignedTo}
            onChange={(event) => setAssignedTo(event.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">{t('customers.assignee.all')}</option>
            <option value={CUSTOMER_UNASSIGNED}>{t('customers.assignee.unassigned')}</option>
            {assignees.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        {/* The people who asked but have not ordered — what the enquiry book used to hold. A
            toggle rather than a select, because it is one question with a yes. */}
        <Button
          variant={enquiriesOnly ? 'default' : 'outline'}
          aria-pressed={enquiriesOnly}
          onClick={toggleEnquiries}
          className="h-9"
        >
          {t('customers.enquiriesFilter')}
        </Button>

        {/* Several at once, so a checklist rather than a select. Every tag the shop manages is
            offered, worn or not, so a tag made a moment ago is here at once. Absent while the
            shop has none: an empty menu is a control that does nothing. */}
        {tags.length > 0 ? (
          <div className="flex flex-col gap-1">
            <span id="customer-tags-label" className="text-sm font-medium">
              {t('customers.filterByTag')}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  aria-labelledby="customer-tags-label customer-tags-value"
                  className="h-9 justify-between gap-2 px-2 font-normal"
                >
                  <span id="customer-tags-value" className="max-w-[10rem] truncate">
                    {activeTags.length === 0
                      ? t('customers.allTags')
                      : activeTags.length === 1
                        ? activeTags[0]
                        : t('customers.tagsChosen', { count: activeTags.length })}
                  </span>
                  <ChevronDown aria-hidden="true" className="size-4 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                {tags.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag.id}
                    checked={activeTags.includes(tag.name)}
                    // Kept open, so ticking three tags is three clicks and not six.
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={() => toggleTag(tag.name)}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mr-2 inline-block size-2.5 shrink-0 rounded-full',
                        TAG_COLOUR_CLASSES[tag.colour].dot,
                      )}
                    />
                    {tag.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}

        {/* A page, not a dialog: what a shop adds from here it usually wants to fill in
            properly. The counter's mid-booking quick add still uses the dialog. */}
        <Button asChild>
          <Link to={createHref} data-tour="customer-add">
            {t('customers.add')}
          </Link>
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <WorkspaceThemeToggle />
          {canEditTags || canExport || canConfigure ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  aria-label={t('customers.menu.more')}
                >
                  <MoreVertical aria-hidden="true" className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[13rem]">
                {canEditTags ? (
                  <DropdownMenuItem onSelect={() => setManagingTags(true)}>
                    <Tags aria-hidden="true" className="size-4" />
                    {t('customers.tags.manageTitle')}
                  </DropdownMenuItem>
                ) : null}
                {/* A plain anchor, not a fetch-and-blob save: the browser's own download
                    machinery has to take the response, or nothing happens at all in the Android
                    WebView. It carries the filter the sheet is showing, so the file matches. */}
                {canExport ? (
                  <DropdownMenuItem asChild>
                    <a href={customerExportPath(searchParams)}>
                      <Download aria-hidden="true" className="size-4" />
                      {t('customers.menu.export')}
                    </a>
                  </DropdownMenuItem>
                ) : null}
                {canConfigure ? (
                  <DropdownMenuItem asChild>
                    <Link to="/settings/custom-fields">
                      <SlidersHorizontal aria-hidden="true" className="size-4" />
                      {t('customers.configureFields')}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {canEditTags ? (
        <ManageCustomerTagsSheet
          open={managingTags}
          onOpenChange={setManagingTags}
          onRenamed={(from, to) => replaceActiveTag(from, to)}
          onRemoved={(tag) => replaceActiveTag(tag, null)}
        />
      ) : null}

      {results.isPending && items.length === 0 ? (
        <div className="p-4 md:min-h-0 md:flex-1">
          <ListSkeleton rows={6} />
        </div>
      ) : results.isError ? (
        // A failed request is NOT an empty result: saying "nobody here" would be a lie.
        <div className="bg-destructive/5 p-8 text-center md:flex md:min-h-0 md:flex-1 md:flex-col md:items-center md:justify-center">
          <p className="text-sm text-destructive">{t('customers.loadFailed')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void results.refetch()}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        // "Nothing matches" and "there is nobody yet" are different answers. The first, when a
        // search was typed, is the counter flow's hand-off: add the person, with what was typed.
        <div className="flex flex-col items-center gap-3 p-8 text-center md:min-h-0 md:flex-1 md:justify-center">
          {query.trim() ? (
            <>
              <p className="text-sm font-medium">{t('customers.noResultsTitle', { query })}</p>
              <p className="text-sm text-muted-foreground">{t('customers.noResultsDescription')}</p>
              <Button onClick={() => navigate(createHref)}>
                {t('customers.addNamed', { query })}
              </Button>
            </>
          ) : narrowed ? (
            <p className="text-sm text-muted-foreground">{t('customers.noFilterResults')}</p>
          ) : (
            <>
              <p className="text-sm font-medium">{t('customers.emptyTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('customers.emptyDescription')}</p>
              {/* A shop with no customers here has them somewhere — offer the import too. */}
              <Button asChild variant="outline">
                <Link to="/customers/import">{t('customers.importList')}</Link>
              </Button>
            </>
          )}
        </div>
      ) : (
        <CustomerTable
          customers={items}
          fields={columns}
          labelOf={labelOf}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
          // The search goes with them, so the way back lands on the list as it was left.
          linkState={{ fromSearch: query }}
          onEdit={setEditing}
          onNewOrder={newOrderFor}
          onAddFollowUp={setFollowingUp}
        />
      )}

      <div className="record-sheet-status flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {/* The four buckets are the shop's figures, not the search's — they do not move as the
            box is typed into. Each says, on hover, which of the shop's own thresholds it was
            counted over; the tiles used to print that under the number. */}
        <dl
          aria-label={t('customers.segments.heading')}
          className="record-sheet-totals flex flex-wrap items-center gap-x-6 gap-y-1"
        >
          <Stat
            label={narrowed ? t('customers.matching') : t('customers.segments.total')}
            value={formatNumber(total)}
          />
          {summary ? (
            <>
              <Stat
                label={t('customers.segments.new')}
                value={formatNumber(summary.new)}
                hint={t('customers.segments.newHint', { days: summary.settings.newWindowDays })}
              />
              <Stat
                label={t('customers.segments.active')}
                value={formatNumber(summary.active)}
                hint={t('customers.segments.activeHint', {
                  days: summary.settings.activeWindowDays,
                })}
              />
              <Stat
                label={t('customers.segments.dormant')}
                value={formatNumber(summary.dormant)}
                hint={t('customers.segments.dormantHint', {
                  days: summary.settings.dormantAfterDays,
                })}
              />
              <Stat
                label={t('customers.segments.highValue')}
                value={formatNumber(summary.highValue)}
                hint={t('customers.segments.highValueHint', {
                  amount: formatCurrency(summary.settings.highValuePaise),
                })}
              />
            </>
          ) : null}
        </dl>

        {total > 0 ? (
          <div className="ml-auto flex items-center gap-2">
            <label htmlFor="customer-page-size" className="text-muted-foreground">
              {t('customers.pagination.perPage')}
            </label>
            <select
              id="customer-page-size"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="h-7 rounded border border-input bg-background px-1.5 text-xs"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              aria-label={t('customers.pagination.previous')}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>

            <span className="tabular-nums text-muted-foreground">
              {t('customers.pagination.range', { first: firstOnPage, last: lastOnPage })}
            </span>

            <button
              type="button"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
              aria-label={t('customers.pagination.next')}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </div>
        ) : null}
      </div>

      {editing ? (
        <CustomerFormDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          customer={editing}
          onSaved={() => setEditing(null)}
        />
      ) : null}

      {followingUp ? (
        <TaskDialog
          open
          onOpenChange={(open) => !open && setFollowingUp(null)}
          subject={{ kind: 'customer', id: followingUp.id }}
        />
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): ReactElement {
  return (
    <div className="flex items-baseline gap-1.5" title={hint}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
